/* eslint-disable menti-react/filename-convention--jsx */
import { useState } from 'react';
import { VotingConfirmationModal } from '@mentimeter/voting-ui';
import type { QuestionWithSlide } from '@mentimeter/voting-schema/api-types-overrides';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Form } from '@mentimeter/ragnar-ui/form';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type { MultipleChoiceStaticContent } from '@core-api/audience/types/response';
import { SubmitVoteFormButton } from '../../ui/Components/SubmitVoteFormButton';
import { TitleSectionWithImage } from '../../ui/Components/VotingTitle';
import { getInteractiveContentChoiceId } from '../../utils/getInteractiveContentChoiceId';
import { useTranslate } from '../../features/localization/translate';
import { useActions } from '../../features/questions/useActions';
import type { ModuleComponentProps } from '../types';
import type { SubmitVoteBody } from '../../types';
import { Options } from './Options';

export function Content({ question }: ModuleComponentProps) {
  return (
    <TitleSectionWithImage
      image={question.image}
      meta={question.title_meta}
      title={question.question}
      titleStyled={question.question_styled}
      description={question.question_description}
    />
  );
}

function createSubmitVoteBody(
  question: QuestionWithSlide,
  rawVote: string | string[], // "choiceId" i.e legacyChoiceId
): SubmitVoteBody {
  if (question.isMigrated) {
    const rawVoteArray = Array.isArray(rawVote) ? rawVote : [rawVote];

    const interactiveContentChoiceIds = rawVoteArray.map((rawVoteChoiceId) => {
      const interactiveContentChoiceId = getInteractiveContentChoiceId(
        rawVoteChoiceId,
        question,
      );
      return { interactiveContentChoiceId };
    });
    return {
      isMigrated: true,
      slidePublicKey: question.slidePublicKey,
      interactiveContentId:
        question.interactiveContents[0]!.interactiveContentId,
      payload: {
        type: 'multiple-choice',
        choices: interactiveContentChoiceIds,
      },
      partial: false,
    };
  }
  return {
    isMigrated: false,
    questionPublicKey: question.slidePublicKey,
    payload: { type: 'choices', vote: rawVote },
    partial: false,
  };
}
export function Interactive({ question: slide }: ModuleComponentProps) {
  const { skip: skipVote, vote: submitVote } = useActions();
  const translate = useTranslate();
  const [showVoteFailedModal, setShowVoteFailedModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [rawVotes, setVotes] = useState<string[]>([]);
  const interactiveContent = slide.interactiveContents[0]!;
  const votes =
    interactiveContent.responsePolicy === 'single-response-limited-choices'
      ? rawVotes.slice(0, interactiveContent.maxEntries || -1)
      : rawVotes;

  const staticContent = slide.staticContent as MultipleChoiceStaticContent;
  const shouldShowChoiceImages =
    staticContent.interactiveContentStyling.visualization === 'bar_chart';

  return (
    <Form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!showConfirmationModal && votes.length === 0) {
          setShowConfirmationModal(true);
        } else {
          const allowMultiple =
            interactiveContent.responsePolicy ===
              'single-response-limited-choices' &&
            interactiveContent.maxEntries !== null &&
            interactiveContent.maxEntries > 1;

          const selectedVote = allowMultiple ? votes : votes[0]!;
          const response = await submitVote(
            createSubmitVoteBody(slide, selectedVote),
          );
          if (response.type === 'submitVote/rejected') {
            setShowVoteFailedModal(true);
            return;
          }
        }
      }}
      width="100%"
    >
      <Box as="fieldset" width="100%">
        <ScreenReaderOnly as="legend">{slide.title}</ScreenReaderOnly>
        <Options
          maxEntries={
            interactiveContent.responsePolicy ===
            'single-response-limited-choices'
              ? (interactiveContent.maxEntries ??
                interactiveContent.choices.length)
              : 1
          }
          choices={interactiveContent.choices.map((choice) => ({
            id: choice.legacyChoiceId ?? 0,
            label: choice.title,
            image: choice.image,
          }))}
          votes={votes}
          setVotes={setVotes}
          isChoiceImageVisible={shouldShowChoiceImages}
        />
      </Box>

      <SubmitVoteFormButton
        isDisabled={
          slide.interactiveContents[0]!.responseMode !== 'accepting-responses'
        }
      />
      <VotingConfirmationModal
        id="skip-vote-modal-choices"
        showModal={showConfirmationModal}
        onConfirm={skipVote}
        onDismiss={() => setShowConfirmationModal(false)}
        title={translate('messages.not_made_choice')}
        bodyText={translate('messages.continue_without_voting')}
        confirmButtonText={translate('buttons.ok')}
        dismissButtonText={translate('buttons.cancel')}
      />
      <VotingConfirmationModal
        showModal={showVoteFailedModal}
        id="vote-modal-vote-failed-choices"
        title={translate('messages.something_went_wrong')}
        bodyText={translate('messages.unexpected_problem')}
        confirmButtonText={translate('buttons.try_again')}
        onConfirm={() => setShowVoteFailedModal(false)}
        onDismiss={() => setShowVoteFailedModal(false)}
      />
    </Form>
  );
}
